import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Globe, Sparkles, Loader2, Save, RefreshCw, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

// SEO + GEO advanced settings — extracted VERBATIM from Settings.js so the new
// BrandDnaView and (if ever re-added) Settings share ONE implementation.
// Save paths unchanged: PUT /settings/brand-dna/seo and PUT /settings/brand-dna/geo.
// Self-contained (own state + own load), single prop { getAuthHeader }. Dark theme.

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

const Toggle = ({ value, onChange, label, description }) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-[#0A1628]/50 last:border-0">
    <div className="flex-1">
      <p className="text-white text-sm font-medium">{label}</p>
      {description && <p className="text-[#5A6C7D] text-xs mt-0.5">{description}</p>}
    </div>
    <button
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out mt-0.5 ${value ? 'bg-[#00D9C8]' : 'bg-[#1A2B42]'}`}
      role="switch"
      aria-checked={value}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${value ? 'translate-x-5' : 'translate-x-0'}`}
      />
    </button>
  </div>
);

const BrandEinstellungen = ({ getAuthHeader }) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [seo, setSeo] = useState({
    seoEnabled: true, keywordsEnabled: true, metaEnabled: true, ctaEnabled: true,
    geoEnabled: true, citableFactsEnabled: true, faqEnabled: true, structuredListsEnabled: true,
    localCity: '', localRegion: '', localCountry: '',
  });

  useEffect(() => {
    axios.get(`${API}/settings/brand-dna`, { headers: getAuthHeader() })
      .then(r => { if (r.data.seoSettings) setSeo(s => ({ ...s, ...r.data.seoSettings })); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings/brand-dna/seo`, seo, { headers: getAuthHeader() });
      toast.success(t.settings.advanced.savedToast);
    } catch { toast.error(t.settings.advanced.saveFailed); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#00D9C8]" /></div>;

  return (
    <div className="space-y-4">
      {/* SEO Toggles */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Globe className="w-4 h-4 text-[#00D9C8]" />{t.settings.advanced.seoTitle}
          </CardTitle>
          <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.advanced.seoDesc}</CardDescription>
        </CardHeader>
        <CardContent>
          <Toggle value={seo.seoEnabled} onChange={(v) => setSeo(s => ({ ...s, seoEnabled: v }))} label={t.settings.advanced.seoEnabled} description={t.settings.advanced.seoEnabledDesc} />
          {seo.seoEnabled && (
            <>
              <Toggle value={seo.keywordsEnabled} onChange={(v) => setSeo(s => ({ ...s, keywordsEnabled: v }))} label={t.settings.advanced.keywordsEnabled} description={t.settings.advanced.keywordsEnabledDesc} />
              <Toggle value={seo.metaEnabled} onChange={(v) => setSeo(s => ({ ...s, metaEnabled: v }))} label={t.settings.advanced.metaEnabled} description={t.settings.advanced.metaEnabledDesc} />
              <Toggle value={seo.ctaEnabled} onChange={(v) => setSeo(s => ({ ...s, ctaEnabled: v }))} label={t.settings.advanced.ctaEnabled} description={t.settings.advanced.ctaEnabledDesc} />
            </>
          )}
        </CardContent>
      </Card>

      {/* GEO Toggles */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#00D9C8]" />{t.settings.advanced.geoTitle}
          </CardTitle>
          <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.advanced.geoDesc}</CardDescription>
        </CardHeader>
        <CardContent>
          <Toggle value={seo.geoEnabled} onChange={(v) => setSeo(s => ({ ...s, geoEnabled: v }))} label={t.settings.advanced.geoEnabled} description={t.settings.advanced.geoEnabledDesc} />
          {seo.geoEnabled && (
            <>
              <Toggle value={seo.citableFactsEnabled} onChange={(v) => setSeo(s => ({ ...s, citableFactsEnabled: v }))} label={t.settings.advanced.citableFacts} description={t.settings.advanced.citableFactsDesc} />
              <Toggle value={seo.faqEnabled} onChange={(v) => setSeo(s => ({ ...s, faqEnabled: v }))} label={t.settings.advanced.faq} description={t.settings.advanced.faqDesc} />
              <Toggle value={seo.structuredListsEnabled} onChange={(v) => setSeo(s => ({ ...s, structuredListsEnabled: v }))} label={t.settings.advanced.lists} description={t.settings.advanced.listsDesc} />
            </>
          )}
        </CardContent>
      </Card>

      {/* Lokale SEO */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center gap-2"><MapPin className="w-4 h-4 text-[#00D9C8]" />{t.settings.advanced.localSeo}</CardTitle>
          <CardDescription className="text-[#A0AEC0] text-sm">{t.settings.advanced.localSeoDesc}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[#5A6C7D] text-xs block mb-1">{t.settings.advanced.city}</label>
              <Input value={seo.localCity || ''} onChange={(e) => setSeo(s => ({ ...s, localCity: e.target.value }))} placeholder={t.settings.advanced.cityPh} className="bg-[#0A1628] border-transparent text-white text-sm" data-testid="geo-city" />
            </div>
            <div>
              <label className="text-[#5A6C7D] text-xs block mb-1">{t.settings.advanced.region}</label>
              <Input value={seo.localRegion || ''} onChange={(e) => setSeo(s => ({ ...s, localRegion: e.target.value }))} placeholder={t.settings.advanced.regionPh} className="bg-[#0A1628] border-transparent text-white text-sm" data-testid="geo-region" />
            </div>
            <div>
              <label className="text-[#5A6C7D] text-xs block mb-1">{t.settings.advanced.country}</label>
              <Input value={seo.localCountry || ''} onChange={(e) => setSeo(s => ({ ...s, localCountry: e.target.value }))} placeholder={t.settings.company.exampleCountry} className="bg-[#0A1628] border-transparent text-white text-sm" data-testid="geo-country" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="w-full bg-[#00D9C8] hover:bg-[#00D9C8]/90 text-[#0A1628] font-semibold" data-testid="save-seo-btn">
        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}{t.settings.advanced.saveSettings}
      </Button>

      {/* Bild-URLs erneuern */}
      <Card className="bg-[#1A2B42] border-transparent">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <RefreshCw className="w-4 h-4 text-amber-400" />
              <div>
                <p className="text-white text-sm font-medium">{t.settings.advanced.refreshImageUrlsTitle}</p>
                <p className="text-[#A0AEC0] text-xs">{t.settings.advanced.refreshImageUrlsDesc}</p>
              </div>
            </div>
            <Button variant="outline" size="sm"
              onClick={async () => {
                try {
                  setSaving(true);
                  await axios.post(`${API}/admin/refresh-image-urls`, {}, { headers: getAuthHeader(), timeout: 60000 });
                  toast.success(t.settings.advanced.refreshedToast);
                } catch { toast.error(t.settings.advanced.refreshFailedToast); }
                finally { setSaving(false); }
              }}
              disabled={saving}
              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
              data-testid="refresh-image-urls-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              {t.settings.advanced.refresh}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BrandEinstellungen;
